import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { theme } from "./theme";
import { buildAxisStyle } from "./AnalyticsConfig";

const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

export function normalizeCohort(apiRows = []) {
  if (!Array.isArray(apiRows)) return [];
  return apiRows.map((r, idx) => {
    const age = Number(firstDefined(r.age, r.Age, r.patient_age));
    return {
      id: firstDefined(r.id, r.patient_id, r.patientId, `row-${idx}`) + `-${idx}`,
      patientName: firstDefined(r.patientName, r.patient_name, r.name, "—"),
      age: Number.isFinite(age) ? age : null,
      dob: firstDefined(r.dob, r.date_of_birth, "") || "",
      insurance: firstDefined(r.insurance, r.primary_insurance, "") || "",
      dxCode: firstDefined(r.dxCode, r.dx_code, r.icd10, r.icd_code_10, "") || "",
      medication: Array.isArray(r.medication)
        ? r.medication.filter(Boolean).map(String)
        : r.medication
          ? [String(r.medication)]
          : [],
      nextAppointment: firstDefined(r.nextAppointment, r.next_appointment) || null,
      provider: firstDefined(r.provider, r.provider_name, "") || "",
      location: firstDefined(r.location, r.facility_name, "") || "",
      dateOfService: firstDefined(r.dateOfService, r.date_of_service, "") || "",
    };
  });
}

export function searchRows(rows, term) {
  const q = (term || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    `${r.patientName} ${r.dxCode} ${r.provider} ${r.location} ${r.insurance}`
      .toLowerCase()
      .includes(q)
  );
}

export const toOptions = (values, allLabel) => [
  { value: "", label: allLabel },
  ...(Array.isArray(values) ? values : [])
    .filter(Boolean)
    .map((v) => ({ value: v, label: v })),
];

export const formatDate = (value) => {
  if (!value) return "—";
  const s = String(value).slice(0, 10);
  let d;
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const [, m, day, y] = mdy;
    d = new Date(`${y}-${m.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`);
  } else {
    d = new Date(`${s}T00:00:00`);
  }
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

export const parseDateTimestamp = (value) => {
  if (!value) return 0;
  const s = String(value).slice(0, 10);
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const [, m, day, y] = mdy;
    return new Date(`${y}-${m.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`).getTime() || 0;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

export const AGE_BANDS = [
  { key: "4-9", label: "4–9", get color() { return theme.vbcAgeBandColors()[1]; } },
  { key: "10-12", label: "10–12", get color() { return theme.vbcAgeBandColors()[2]; } },
  { key: "13-17", label: "13–17", get color() { return theme.vbcAgeBandColors()[3]; } },
  { key: "18-21", label: "18–21", get color() { return theme.vbcAgeBandColors()[4]; } },
  { key: "22-30", label: "22–30", get color() { return theme.vbcAgeBandColors()[5]; } },
  { key: "31-40", label: "31–40", get color() { return theme.vbcAgeBandColors()[6]; } },
  { key: "41-50", label: "41–50", get color() { return theme.vbcAgeBandColors()[7]; } },
  { key: "51-60", label: "51–60", get color() { return theme.vbcAgeBandColors()[8]; } },
  { key: "61-70", label: "61–70", get color() { return theme.vbcAgeBandColors()[9]; } },
  { key: "71-80", label: "71–80", get color() { return theme.vbcAgeBandColors()[10]; } },
  { key: "80+", label: "80 and above", get color() { return theme.vbcAgeBandColors()[11]; } },
];

const BAND_LABEL_MAP = Object.fromEntries(AGE_BANDS.map((b) => [b.key, b.label]));

const bandColor = (key) => {
  const found = AGE_BANDS.find((b) => b.key === key);
  return found?.color || theme.vbcAgeBandColors()[0];
};

const barBandColor = (key, opacity = 0.80) => {
  const hex = bandColor(key);
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const hexToRgb = (hex) => {
  const c = String(hex || "#000000").replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return {
    r: Number.isNaN(r) ? 0 : r,
    g: Number.isNaN(g) ? 0 : g,
    b: Number.isNaN(b) ? 0 : b,
  };
};

const getCellColor = (bandKey, count, maxVal) => {
  if (!count) return theme.vbcHeatmapEmpty();
  const baseHex = bandColor(bandKey);
  const { r, g, b } = hexToRgb(baseHex);
  const max = Math.max(maxVal, 1);
  const ratio = Math.min(1, Math.max(0, count / max));
  const alpha = 0.26 + 0.74 * Math.pow(ratio, 0.65);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};

const getCellTextColor = (count, maxVal) => {
  if (!count) return "transparent";
  const max = Math.max(maxVal, 1);
  const ratio = count / max;
  return ratio > 0.45 ? theme.chartWhite() : theme.chartTooltipText();
};

/** Prescription detail is "<drug> <strength> <form> <sig…>" — drop the sig for a chart label. */
const SIG_RE =
  /\s+(take|use|apply|inject|inhale|instill|chew|dissolve|place|spray|swallow|give)\b.*$/i;
export const drugLabel = (detail) => {
  const s = String(detail || "").trim();
  if (!s) return "";
  return s.replace(SIG_RE, "").trim().slice(0, 80);
};

export function getAgeBandKey(age) {
  const a = Number(age);
  if (!Number.isFinite(a)) return null;
  if (a <= 9) return "4-9";
  if (a <= 12) return "10-12";
  if (a <= 17) return "13-17";
  if (a <= 21) return "18-21";
  if (a <= 30) return "22-30";
  if (a <= 40) return "31-40";
  if (a <= 50) return "41-50";
  if (a <= 60) return "51-60";
  if (a <= 70) return "61-70";
  if (a <= 80) return "71-80";
  return "80+";
}

/**
 * Aggregates patient cohort data into the 11 age intervals grouped by ICD-10 code.
 */
export function buildAgeVsDxFromCohort(cohort = []) {
  if (!Array.isArray(cohort) || !cohort.length) return null;

  const dxMap = new Map();
  cohort.forEach((row) => {
    const dx = row.dxCode || "—";
    if (!dxMap.has(dx)) {
      dxMap.set(dx, { total: 0, counts: {} });
    }
    const item = dxMap.get(dx);
    item.total += 1;
    const band = getAgeBandKey(row.age);
    if (band) {
      item.counts[band] = (item.counts[band] || 0) + 1;
    }
  });

  const sortedDx = [...dxMap.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .map(([dx]) => dx);

  const bands = AGE_BANDS.map((b) => b.key);
  const series = bands.map((band) => ({
    band,
    data: sortedDx.map((dx) => dxMap.get(dx)?.counts[band] || 0),
  }));

  return {
    dxCodes: sortedDx,
    bands,
    series,
  };
}

/**
 * Aggregates patient cohort data into the 11 age intervals grouped by medication.
 * Same shape as buildAgeVsDxFromCohort but keyed on the drug label (sig stripped).
 * A drug listed twice on one row counts once for that row.
 */
export function buildAgeVsMedicationFromCohort(cohort = []) {
  if (!Array.isArray(cohort) || !cohort.length) return null;

  const medMap = new Map();
  cohort.forEach((row) => {
    const band = getAgeBandKey(row.age);
    const meds = Array.isArray(row.medication)
      ? row.medication
      : row.medication
        ? [row.medication]
        : [];
    const labels = new Set(meds.map(drugLabel).filter(Boolean));
    labels.forEach((label) => {
      if (!medMap.has(label)) medMap.set(label, { total: 0, counts: {} });
      const item = medMap.get(label);
      item.total += 1;
      if (band) item.counts[band] = (item.counts[band] || 0) + 1;
    });
  });

  if (!medMap.size) return null;

  const sorted = [...medMap.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .map(([m]) => m);

  const bands = AGE_BANDS.map((b) => b.key);
  const series = bands.map((band) => ({
    band,
    data: sorted.map((m) => medMap.get(m)?.counts[band] || 0),
  }));

  return { medications: sorted, bands, series };
}

const VISIBLE_ROWS = 13;

export function ageVsDxOption(ageVsDx) {
  if (!ageVsDx || !Array.isArray(ageVsDx.dxCodes) || !ageVsDx.dxCodes.length) return null;

  const n = ageVsDx.dxCodes.length;
  const cats = [...ageVsDx.dxCodes].reverse();
  const bands =
    Array.isArray(ageVsDx.bands) && ageVsDx.bands.length
      ? ageVsDx.bands
      : AGE_BANDS.map((b) => b.key);
  const seriesByBand = new Map((ageVsDx.series || []).map((s) => [s.band, s.data || []]));

  const series = bands.map((band) => ({
    name: BAND_LABEL_MAP[band] || band,
    type: "bar",
    stack: "age",
    emphasis: {
      focus: "series",
      itemStyle: {
        color: bandColor(band),
      },
    },
    itemStyle: {
      color: barBandColor(band, 0.80),
      borderColor: theme.chartSurface(),
      borderWidth: 1,
    },
    barMaxWidth: 18,
    data: [...(seriesByBand.get(band) || [])].reverse(),
  }));

  const windowPct = Math.min(100, (VISIBLE_ROWS / n) * 100);
  const scrollable = n > VISIBLE_ROWS;

  return {
    title: { show: false },
    grid: { left: 12, right: scrollable ? 46 : 16, top: 68, bottom: 44, containLabel: true },
    legend: {
      type: "scroll",
      top: 6,
      left: 8,
      padding: [0, 96, 0, 0],
      itemWidth: 11,
      itemHeight: 11,
      itemGap: 8,
      textStyle: { color: theme.chartAxisLabel(), fontSize: 11 },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: theme.chartTooltipBg(),
      borderColor: theme.chartTooltipBorder(),
      borderWidth: 1,
      textStyle: { color: theme.chartTooltipText(), fontSize: 12 },
      extraCssText: "box-shadow: 0 10px 28px rgba(15,23,42,0.16); border-radius: 10px;",
      formatter: (params) => {
        if (!params || !params.length) return "";
        const total = params.reduce((s, p) => s + (p.value || 0), 0);
        const rows = params
          .filter((p) => p.value)
          .map(
            (p) =>
              `<div style="display:flex;justify-content:space-between;gap:16px"><span>${p.marker} ${p.seriesName}</span><b>${p.value}</b></div>`
          )
          .join("");
        return `<div style="font-weight:700;margin-bottom:4px">${params[0].axisValue}</div>${rows}<div style="margin-top:4px;border-top:1px solid ${theme.chartTooltipBorder()};padding-top:4px;display:flex;justify-content:space-between;gap:16px"><span>Total patients</span><b>${total}</b></div>`;
      },
    },
    xAxis: {
      type: "value",
      name: "Patients",
      nameLocation: "middle",
      nameGap: 30,
      nameTextStyle: { color: theme.chartAxisLabel(), fontSize: 11 },
      minInterval: 1,
      ...buildAxisStyle(),
    },
    yAxis: {
      type: "category",
      data: cats,
      axisLabel: {
        color: theme.chartAxisLabel(),
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    },
    dataZoom: scrollable
      ? [
        {
          type: "slider",
          yAxisIndex: 0,
          right: 8,
          top: 68,
          bottom: 44,
          width: 14,
          start: 100 - windowPct,
          end: 100,
          showDetail: false,
          brushSelect: false,
          handleSize: "70%",
          borderColor: theme.chartZoomTrackBorder(),
          backgroundColor: theme.chartZoomTrackBg(),
          fillerColor: "rgba(60, 159, 162, 0.20)",
        },
        {
          type: "inside",
          yAxisIndex: 0,
          start: 100 - windowPct,
          end: 100,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: false,
        },
      ]
      : undefined,
    series,
    animationDuration: 600,
    animationEasing: "cubicOut",
  };
}

/**
 * Age vs Medication — a heatmap matrix of medications (rows) across the 11 age intervals (columns).
 * Each column is tinted with its corresponding age-band color from the Age vs Dx chart.
 * 0-count cells have a visible neutral grey shade (#e2e8f0) so the matrix boundary is clear.
 * Features: vertical scrolling (dataZoom), fixed top X-axis with age-band colors, and clean direct numbers.
 */
export function ageVsMedicationOption(data) {
  if (!data || !Array.isArray(data.medications) || !data.medications.length) return null;

  const meds = data.medications;
  const n = meds.length;
  const bandKeys =
    Array.isArray(data.bands) && data.bands.length ? data.bands : AGE_BANDS.map((b) => b.key);
  const bandLabels = bandKeys.map((b) => BAND_LABEL_MAP[b] || b);
  const seriesByBand = new Map((data.series || []).map((s) => [s.band, s.data || []]));

  // Y-axis renders bottom-up in ECharts, so reverse so highest frequency medication is at top
  const cats = [...meds].reverse();

  let maxVal = 0;
  bandKeys.forEach((band) => {
    (seriesByBand.get(band) || []).forEach((v) => {
      if (v > maxVal) maxVal = v;
    });
  });

  const cells = [];
  cats.forEach((med, rowIdx) => {
    const medIdx = meds.indexOf(med);
    bandKeys.forEach((band, colIdx) => {
      const val = (seriesByBand.get(band) || [])[medIdx] || 0;
      cells.push({
        value: [colIdx, rowIdx, val],
        itemStyle: {
          color: getCellColor(band, val, maxVal),
          borderColor: theme.chartSurface(),
          borderWidth: 1.5,
          borderRadius: 2,
        },
        label: {
          show: val > 0,
          color: getCellTextColor(val, maxVal),
          fontWeight: 700,
          fontSize: 10.5,
        },
      });
    });
  });

  const VISIBLE_ROWS = 12;
  const windowPct = Math.min(100, (VISIBLE_ROWS / n) * 100);
  const scrollable = n > VISIBLE_ROWS;

  return {
    title: { show: false },
    grid: {
      left: 12,
      right: scrollable ? 46 : 16,
      top: 50,
      bottom: 16,
      containLabel: true,
    },
    visualMap: {
      show: false,
      min: 0,
      max: Math.max(maxVal, 1),
    },
    tooltip: {
      position: "top",
      backgroundColor: theme.chartTooltipBg(),
      borderColor: theme.chartTooltipBorder(),
      borderWidth: 1,
      textStyle: { color: theme.chartTooltipText(), fontSize: 12 },
      extraCssText: "box-shadow: 0 10px 28px rgba(15,23,42,0.16); border-radius: 10px;",
      formatter: (p) => {
        if (!p || !p.value) return "";
        const [colIdx, rowIdx, value] = p.value;
        const medName = cats[rowIdx] || "";
        const bandKey = bandKeys[colIdx];
        const ageBand = bandLabels[colIdx] || bandKey;
        const color = bandColor(bandKey);
        const dot = `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background-color:${color};"></span>`;
        return (
          `<div style="font-weight:700;margin-bottom:4px">${medName}</div>` +
          `<div style="display:flex;justify-content:space-between;gap:16px;align-items:center;"><span>${dot}Age Group:</span><b>${ageBand}</b></div>` +
          `<div style="display:flex;justify-content:space-between;gap:16px;margin-top:2px;"><span>Patients:</span><b>${value || 0} patient${value === 1 ? "" : "s"}</b></div>`
        );
      },
    },
    xAxis: {
      type: "category",
      data: bandLabels,
      position: "top",
      splitArea: { show: false },
      axisLabel: {
        interval: 0, // Ensure all 11 age group labels show
        fontSize: 11,
        fontWeight: 700,
        margin: 8,
        color: (_val, idx) => bandColor(bandKeys[idx]) || theme.chartAxisLabel(),
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    },
    yAxis: {
      type: "category",
      data: cats,
      splitArea: { show: false },
      axisLabel: {
        color: theme.chartAxisLabel(),
        fontSize: 11,
        width: 220,
        overflow: "truncate",
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    },
    dataZoom: scrollable
      ? [
        {
          type: "slider",
          yAxisIndex: 0,
          right: 8,
          top: 50,
          bottom: 16,
          width: 14,
          start: 100 - windowPct,
          end: 100,
          showDetail: false,
          brushSelect: false,
          handleSize: "70%",
          borderColor: theme.chartZoomTrackBorder(),
          backgroundColor: theme.chartZoomTrackBg(),
          fillerColor: "rgba(60, 159, 162, 0.20)",
        },
        {
          type: "inside",
          yAxisIndex: 0,
          start: 100 - windowPct,
          end: 100,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: false,
        },
      ]
      : undefined,
    series: [
      {
        name: "Patients",
        type: "heatmap",
        data: cells,
        label: {
          show: true,
          fontSize: 10.5,
          fontWeight: 700,
          formatter: (p) => (p.value[2] > 0 ? String(p.value[2]) : ""),
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(15, 23, 42, 0.35)",
            borderColor: "#0f172a",
            borderWidth: 2,
          },
        },
      },
    ],
    animationDuration: 600,
    animationEasing: "cubicOut",
  };
}

const COLUMNS = [
  { key: "patientName", label: "Patient Name" },
  { key: "age", label: "Age" },
  { key: "dxCode", label: "Dx - ICD-10" },
  {
    key: "medication",
    label: "Medication",
    fmt: (v) => (Array.isArray(v) ? v.join("; ") : v || ""),
  },
  { key: "nextAppointment", label: "Next Appointment", fmt: formatDate },
  { key: "provider", label: "Provider" },
  { key: "location", label: "Location" },
  { key: "dateOfService", label: "Date of Service", fmt: formatDate },
];

const cellValue = (row, col) => {
  if (col.pending) return "";
  const val = row[col.key];
  if (col.key === "medication") {
    if (Array.isArray(val)) return val.filter(Boolean).join(" ; ");
    if (val === null || val === undefined || val === "—") return "";
    return String(val);
  }
  const raw = col.fmt ? col.fmt(val) : val;
  if (raw === null || raw === undefined || raw === "—") return "";
  return String(raw);
};

const slug = (s) =>
  String(s || "")
    .trim()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "") || "all";

const clipText = (value, max) => {
  const s = String(value || "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const baseName = (meta) =>
  `Value-Based-Care_${slug(meta.insuranceLabel)}_${slug(meta.startDate)}_${slug(meta.endDate)}`;

const htmlEscape = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/**
 * Generates an Excel-compatible spreadsheet (.xls) with full table borders,
 * colored headers (#0E7A80), formatted metadata summary, and zebra rows.
 */
export function exportCohortToExcel({ rows, meta }) {
  const tableHeaders = COLUMNS.map(
    (c) =>
      `<th style="background-color: #0E7A80; color: #ffffff; font-weight: bold; font-size: 11pt; border: 1.5px solid #000000; padding: 8px; text-align: ${c.key === "age" ? "center" : "left"
      };">${htmlEscape(c.label)}</th>`
  ).join("");

  const tableRowsHtml = rows
    .map((row, idx) => {
      const bg = idx % 2 === 0 ? "#ffffff" : "#f0f7f8";
      const cells = COLUMNS.map((c) => {
        const v = cellValue(row, c);
        const align = c.key === "age" ? "center" : "left";
        return `<td style="background-color: ${bg}; border: 1px solid #708090; padding: 6px; font-size: 10pt; text-align: ${align}; color: #000000;">${htmlEscape(
          v || "—"
        )}</td>`;
      }).join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const excelContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Patient Group</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; }
        th { background-color: #0E7A80; color: #FFFFFF; font-weight: bold; border: 1.5px solid #000000; padding: 8px; }
        td { border: 1px solid #708090; padding: 6px; }
        .meta-title { font-size: 14pt; font-weight: bold; color: #0E7A80; padding: 8px 0; }
        .meta-lbl { font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8; padding: 5px 8px; color: #0f172a; }
        .meta-val { border: 1px solid #94a3b8; padding: 5px 8px; }
        .meta-note { font-style: italic; background-color: #fffbe8; color: #856404; border: 1px solid #f2e394; padding: 5px 8px; }
      </style>
    </head>
    <body>
      <table border="1" style="border-collapse: collapse;">
        <tr><td colspan="${COLUMNS.length}" class="meta-title" style="font-size: 15pt; font-weight: bold; color: #0E7A80; border: none; padding: 8px 0;">Aegis Health 360 — Value-Based Care Patient Group</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Insurance</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.insuranceLabel)}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Provider</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.providerLabel)}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Location</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.locationLabel)}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Diagnosis (ICD-10)</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.dxCode || "All diagnoses")}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Medication</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.medication || "All medications")}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Date of Service</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${formatDate(meta.startDate)} to ${formatDate(meta.endDate)}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Patients in results</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(String(meta.patientCount ?? rows.length))}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #e6f3f4; border: 1px solid #94a3b8;">Generated</td><td colspan="${COLUMNS.length - 1}" class="meta-val" style="border: 1px solid #94a3b8;">${htmlEscape(meta.generatedAt)}</td></tr>
        <tr><td class="meta-lbl" style="font-weight: bold; background-color: #fffbe8; color: #856404; border: 1px solid #f2e394;">Note</td><td colspan="${COLUMNS.length - 1}" class="meta-note" style="background-color: #fffbe8; color: #856404; border: 1px solid #f2e394;">Medication = prescriptions dispensed within 6 days of the date of service. Next Appointment = the patient's next upcoming booking.</td></tr>
        <tr><td colspan="${COLUMNS.length}" style="border: none; height: 14px;"></td></tr>
        <thead>
          <tr>${tableHeaders}</tr>
        </thead>
        <tbody>
          ${tableRowsHtml}
        </tbody>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob([excelContent], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });
  triggerDownload(blob, `${baseName(meta)}.xls`);
}

export function exportCohortToCsv({ rows, meta }) {
  exportCohortToExcel({ rows, meta });
}

/**
 * Direct client-side PDF generation via jsPDF & jspdf-autotable.
 * Downloads the .pdf file directly with table borders, theme colors, metadata, and optional chart.
 */
export function exportCohortToPdf({ rows, meta, chart = null }) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36; // 0.5 in

  // 1. Header & Brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(14, 122, 128); // #0E7A80
  doc.text("Aegis Health 360", margin, margin + 4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(90, 110, 124);
  doc.text("VALUE-BASED CARE · INCENTIVE PROGRAM", margin, margin + 16);

  // Title & Timestamp on right side
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20, 32, 43);
  doc.text(`${meta.insuranceLabel} — Patient Group`, pageWidth - margin, margin + 4, {
    align: "right",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 110, 124);
  const genInfo = `Generated ${meta.generatedAt}${meta.generatedBy ? ` · ${meta.generatedBy}` : ""}`;
  doc.text(genInfo, pageWidth - margin, margin + 16, { align: "right" });

  // Top Divider line
  doc.setDrawColor(14, 122, 128);
  doc.setLineWidth(2);
  doc.line(margin, margin + 24, pageWidth - margin, margin + 24);

  // 2. Metadata Summary Card
  const metaY = margin + 32;
  const metaHeight = 32;
  const cardWidth = pageWidth - margin * 2;

  // Background Box
  doc.setFillColor(244, 249, 249);
  doc.setDrawColor(201, 226, 228);
  doc.setLineWidth(0.75);
  doc.roundedRect(margin, metaY, cardWidth, metaHeight, 3, 3, "FD");

  // Left Teal Accent
  doc.setFillColor(14, 122, 128);
  doc.rect(margin, metaY, 3.5, metaHeight, "F");

  // Metadata items
  const metaItems = [
    { label: "INSURANCE", val: meta.insuranceLabel },
    { label: "PROVIDER", val: meta.providerLabel },
    { label: "LOCATION", val: meta.locationLabel },
    meta.dxCode && { label: "DX", val: meta.dxCode },
    meta.medication && { label: "MEDICATION", val: clipText(meta.medication, 40) },
    { label: "DATE OF SERVICE", val: `${formatDate(meta.startDate)} to ${formatDate(meta.endDate)}` },
    { label: "PATIENTS", val: String(meta.patientCount ?? rows.length) },
  ].filter(Boolean);

  let curX = margin + 12;
  metaItems.forEach((item) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(90, 110, 124);
    doc.text(item.label + ":", curX, metaY + 13);

    const lblW = doc.getTextWidth(item.label + ": ");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(14, 122, 128);
    doc.text(item.val, curX + lblW, metaY + 13);

    const totalW = lblW + doc.getTextWidth(item.val) + 16;
    curX += totalW;
  });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(133, 93, 10);
  doc.text(
    "* Medication = prescriptions dispensed within 6 days of the DOS. Next Appointment = the patient's next upcoming booking.",
    margin + 12,
    metaY + 25
  );

  // 3. Table Generation with full borders and colors
  const tableHead = [COLUMNS.map((c) => c.label)];
  const tableBody = rows.length
    ? rows.map((row) => COLUMNS.map((c) => cellValue(row, c) || "—"))
    : [
      [
        {
          content: "No patients match the selected filters.",
          colSpan: COLUMNS.length,
          styles: { halign: "center", fontStyle: "italic" },
        },
      ],
    ];

  autoTable(doc, {
    head: tableHead,
    body: tableBody,
    startY: metaY + metaHeight + 10,
    margin: { left: margin, right: margin, bottom: 32 },
    theme: "grid",
    tableWidth: "auto",
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
      lineColor: [180, 195, 205],
      lineWidth: 0.75,
      textColor: [20, 32, 43],
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [14, 122, 128],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      lineColor: [0, 0, 0],
      lineWidth: 1,
      halign: "left",
    },
    alternateRowStyles: {
      fillColor: [244, 249, 250],
    },
    columnStyles: {
      0: { cellWidth: 110, fontStyle: "bold" },
      1: { cellWidth: 38, halign: "center" },
      2: { cellWidth: 70, textColor: [14, 122, 128], fontStyle: "bold" },
      3: { cellWidth: 80 },
      4: { cellWidth: 80 },
      5: { cellWidth: 100 },
      6: { cellWidth: 100 },
      7: { cellWidth: 85 },
    },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);

      doc.setDrawColor(205, 221, 227);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);

      doc.text(
        "Confidential — contains protected health information. Handle per HIPAA policy.",
        margin,
        pageHeight - 12
      );

      doc.text(`Page ${data.pageNumber}`, pageWidth - margin, pageHeight - 12, {
        align: "right",
      });
    },
  });

  // Save & Download directly
  doc.save(`${baseName(meta)}.pdf`);
}
